import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog, DialogActions, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogTrigger,
  Dropdown,
  Field,
  Input,
  Link,
  Option,
  Switch,
  Textarea,
  Tooltip,
  makeStyles,
} from '@fluentui/react-components';
import { FolderArrowRight28Regular, Info16Regular, Open16Regular } from '@fluentui/react-icons';
import { ExportType, isExportTypeDisabled } from '../../types/ExportTypes';
import { useHasCoretextTeamAccess } from '../../utils/UserContext';

// URL to Word Doc with instructions for setting up Xandr Shell ID
const XANDR_SETUP_DOC_URL = 'https://aka.ms/gsc/xandr-shellid';

interface FormValues {
  exportType?: ExportType;
  datasetName: string;
  description: string;
  requiredCheckboxes: { [key: string]: boolean };
  isAutoRefresh: boolean;
  shellId: string;
}

interface CheckboxItemMetadata {
  displayText: string;
  required: boolean;
}

interface ExportTypeOption {
  key: ExportType;
  text: string;
  description: string;
  disabled: boolean;
  supportsAutoRefresh: boolean;
}

interface PopupProps {
  dataset: string;
  onSubmit: (exportType: ExportType, datasetName: string, description: string, isAutoRefresh: boolean, shellId?: string) => void;
  disabled?: boolean;
  disabledReason?: string;
}

const ExportPopUp: React.FC<PopupProps> = ({
  dataset,
  onSubmit,
  disabled = false,
  disabledReason
}) => {
  const styles = useStyles();
  const hasCoretextAccess = useHasCoretextTeamAccess(); // Used for showing auto-refresh feature, which is currently in internal testing
  
  // ========= State variables
  const [formValues, setFormValues] = useState<FormValues>({
    exportType: undefined,
    datasetName: '',
    description: '',
    requiredCheckboxes: {},
    isAutoRefresh: false,
    shellId: ''
  });
  const [showNameError, setShowNameError] = useState(false);
  const [showShellIdError, setShowShellIdError] = useState(false);
  const [showDescError, setShowDescError] = useState(false);
  const [showCheckboxError, setShowCheckboxError] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // This object contains metadata for all of the checkbox items in the export form, including the 
  // 'friendly name' (label), the display text, and whether the checkbox is required.
  const allCheckboxMetadata: { [key: string]: CheckboxItemMetadata } = {
    "EXPORT_ADULT_ONLY": {
      displayText: "I understand that the exported base dataset will include only 'Adult’ accounts. \
          If you want to include ‘Child’/’Teen’ accounts, please reach out to Casie Schuller (casieo) \
          or Shubham Roy (shroy) via Teams/Email. If emailing, please put coretext@microsoft.com on CC.",
      required: true    },
    "EXPORT_AUTO_REFRESH_EXPIRY": {
      displayText: "I acknowledge this auto-refresh export is only valid for 60 days. If I need the export \
          to continue beyond this period, I will need to submit a new request. Note that the custom expiration \
          date is coming soon.",
      required: true    }
  };

  // Records the CHECKED STATE of all checkbox items shown at the bottom of the form. Adding an
  // item here (with accompanying metadata above) will automatically add it to the form UI.
  const [requiredCheckboxItems, setRequiredCheckboxItems] = useState<{ [key: string]: boolean }>({
    "EXPORT_ADULT_ONLY": false,
    "EXPORT_AUTO_REFRESH_EXPIRY": false
  });

  // ========= Form helper functions
  // Base Dataset export type dropdown options
  const exportTypeOptions: ExportTypeOption[] = [
    { key: ExportType.Iris, text: 'IRIS', description: 'Marketing campaigns via IRIS for Mobile, PC, and Xbox-in-product channels', disabled: isExportTypeDisabled(dataset, ExportType.Iris), supportsAutoRefresh: true },
    { key: ExportType.OfferWallet, text: 'Offer Wallet', description: 'Digital wallet for discovering, redeeming, and managing promotional offers', disabled: isExportTypeDisabled(dataset, ExportType.OfferWallet), supportsAutoRefresh: true },
    { key: ExportType.PublisherCampaigns, text: 'Targeted Publisher Offers', description: 'Special discount rates for Xbox gamers based on purchase history, usage, or wish lists', disabled: isExportTypeDisabled(dataset, ExportType.PublisherCampaigns), supportsAutoRefresh: true },
    { key: ExportType.UserResearch, text: 'XR Survey', description: 'Xbox Research gameplay studies for usability feedback and player sentiment', disabled: isExportTypeDisabled(dataset, ExportType.UserResearch), supportsAutoRefresh: true },
    { key: ExportType.GroupsExport, text: 'Groups Export', description: 'Export to Groups, usable in Campsite', disabled: isExportTypeDisabled(dataset, ExportType.GroupsExport), supportsAutoRefresh: false },
    { key: ExportType.Xandr, text: 'Gaming Ads / Xandr', description: 'Base Dataset exported for Gaming Ads campaigns using Xandr Monetize', disabled: isExportTypeDisabled(dataset, ExportType.Xandr), supportsAutoRefresh: true },
  ];
  if (hasCoretextAccess) {
    exportTypeOptions.push(
      { key: ExportType.InternalTest, text: '🛠️ Test to CSV (to gscbasedatasets-dev container only)', description: '', disabled: isExportTypeDisabled(dataset, ExportType.InternalTest), supportsAutoRefresh: true },
      { key: ExportType.InternalADFTest, text: '🛠️ Test Iris to ADF (full pipeline test for Iris)', description: '', disabled: isExportTypeDisabled(dataset, ExportType.InternalADFTest), supportsAutoRefresh: true }
    );
  };
  const resetErrorStates = () => {
    setShowNameError(false);
    setShowShellIdError(false);
    setShowDescError(false);
    setShowCheckboxError(false);
  };
  const resetFormValues = (exportType?: ExportType) => {
    // Reset all form values to default/empty states
    // If exportType is provided, set that as the selected export type; otherwise, clear it.
    setFormValues({
      exportType: exportType,
      datasetName: '',
      description: '',
      requiredCheckboxes: {},
      isAutoRefresh: false,
      shellId: ''
    });
    setRequiredCheckboxItems({
      "EXPORT_ADULT_ONLY": false,
      "EXPORT_AUTO_REFRESH_EXPIRY": false
    });
  };

  // Export type dropdown change handler
  const handleFluentDropdownChange = (event: any, data: any) => {
    if (data.optionValue===formValues.exportType) {
      return; // No change in selection
    } else {
      resetFormValues(data.optionValue);
    }
  };

  // Change handler for whenever dialog is opened or closed
  const handleDialogOpenChange = (event: any, data: { open: boolean }) => {
    setExportDialogOpen(data.open);
    if (data.open) { // Reset form values and error states when dialog is opened
      resetFormValues();
      resetErrorStates();
    }
  }

  // Validates the form inputs before submission. Returns true if any validation fails, false otherwise.
  const validateFormInputs = () => {
    const isXandr = formValues.exportType === ExportType.Xandr;
    let haltSubmission = false;

    if (!formValues.exportType) {
      return true;
    }

    if (isXandr) {
      // Xandr: validate shellId
      if (formValues.shellId.trim() === '' || /[^a-zA-Z0-9\-_]/.test(formValues.shellId)) {
        setShowShellIdError(true);
        haltSubmission = true;
      }
    }

    // Standard: validate datasetName
    const dataSetMalFormed = /[^a-zA-Z0-9\-_]/.test(formValues.datasetName);
    if (formValues.datasetName.trim() === "" || dataSetMalFormed) {
      setShowNameError(true);
      haltSubmission = true;
    }

    // Description error
    if (formValues.description.trim() === "") {
      setShowDescError(true);
      haltSubmission = true;
    }
    // Checkbox error - only validate checkboxes that are currently visible
    const checkboxesToValidate = Object.entries(requiredCheckboxItems).filter(([key, _]) => {
      // EXPORT_AUTO_REFRESH_EXPIRY is only required when auto-refresh is enabled
      if (key === "EXPORT_AUTO_REFRESH_EXPIRY") {
        return formValues.isAutoRefresh;
      }
      return true; // All other checkboxes are always required
    });
    if (!checkboxesToValidate.every(([_, isChecked]) => isChecked)) {
      setShowCheckboxError(true);
      haltSubmission = true;
    }
    return haltSubmission;
  };

  // Form Submission Handler
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Reset error states from previous submissions
    resetErrorStates();

    let haltSubmission = validateFormInputs();

    if (!haltSubmission) {
      onSubmit(
        formValues.exportType as ExportType,
        formValues.datasetName,
        formValues.description,
        formValues.isAutoRefresh,
        formValues.exportType === ExportType.Xandr ? formValues.shellId : undefined
      );
      setExportDialogOpen(false);
    }
  };

  // Rendered as the dialog trigger when enabled, or as a standalone disabled button when not.
  // `disabledFocusable` keeps the button hoverable and focusable so the explanatory tooltip is
  // still reachable (a plain `disabled` button suppresses the pointer events Tooltip relies on)
  // while preventing activation.
  const requestButton = (
    <Button
      appearance="primary"
      icon={<FolderArrowRight28Regular />}
      className={styles.requestButton}
      disabledFocusable={disabled}
    >
      Request Base Dataset
    </Button>
  );

  // Bypass the Dialog entirely when disabled so the export form cannot be opened at all.
  if (disabled) {
    return (
      <Tooltip
        content={disabledReason ?? ''}
        relationship="description"
        positioning="below-end"
      >
        {requestButton}
      </Tooltip>
    );
  }

  return (
    <Dialog open={exportDialogOpen} onOpenChange={handleDialogOpenChange} modalType="modal">
      <DialogTrigger disableButtonEnhancement>
        {requestButton}
      </DialogTrigger>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle>
            Request Base Dataset
          </DialogTitle>
          <DialogContent>
              <form onSubmit={handleSubmit}>
                <Field
                  label="I am requesting a Base Dataset for"
                  className={styles.exportFormField}>
                  <Dropdown
                    onOptionSelect={handleFluentDropdownChange}
                    placeholder="Select export type"
                  >
                    {exportTypeOptions.map(opt => (
                        <Option key={opt.key} value={opt.key} disabled={opt.disabled} text={opt.text}
                          className={opt.key == ExportType.InternalTest || opt.key == ExportType.InternalADFTest ? styles.internalTestOption : ''}>
                          <div>
                            {opt.text}<br/>
                            <span className={styles.exportTypeDescription}>{opt.description}</span>
                          </div>
                        </Option>
                    ))}
                  </Dropdown>
                </Field>

                { /* --- Form Body --- */
                formValues.exportType && (
                  <ExportFormTemplate
                    formValues={formValues}
                    setFormValues={setFormValues}
                    showNameError={showNameError}
                    showShellIdError={showShellIdError}
                    showDescError={showDescError}
                    showCheckboxError={showCheckboxError}
                    requiredCheckboxItems={requiredCheckboxItems}
                    setRequiredCheckboxItems={setRequiredCheckboxItems}
                    allCheckboxMetadata={allCheckboxMetadata}
                    exportTypeOptions={exportTypeOptions}
                  />
                )}
              
                {!!formValues.exportType && (
                  <DialogActions>
                    <DialogTrigger disableButtonEnhancement>
                      <Button appearance="secondary">Cancel</Button>
                    </DialogTrigger>
                    
                    <Tooltip
                      content={
                        formValues.isAutoRefresh
                          ? "Submit an auto-refresh export that will re-generate your base dataset with the latest data."
                          : "Submit a one-time export with no automatic updates. Dataset will expire after 30 days."
                      }
                      relationship="description"
                      positioning="below-end"
                    >
                      <Button type="submit" appearance="primary" onClick={handleSubmit}
                        className={formValues.isAutoRefresh ? styles.autoRefreshSubmitButton : ''}>
                        {formValues.isAutoRefresh ? "Submit Auto-Refresh Export" : "Submit"}
                      </Button>
                    </Tooltip>
                  </DialogActions>
                )}
              </form>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};



interface ExportFormTemplateProps {
  formValues: FormValues;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  showNameError: boolean;
  showShellIdError: boolean;
  showDescError: boolean;
  showCheckboxError: boolean;
  requiredCheckboxItems: { [key: string]: boolean };
  setRequiredCheckboxItems: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  allCheckboxMetadata: { [key: string]: CheckboxItemMetadata };
  exportTypeOptions: ExportTypeOption[];
}

const ExportFormTemplate: React.FC<ExportFormTemplateProps> = ({
  formValues,
  setFormValues,
  showNameError,
  showShellIdError,
  showDescError,
  showCheckboxError,
  requiredCheckboxItems,
  setRequiredCheckboxItems,
  allCheckboxMetadata,
  exportTypeOptions
}) => {
  const styles = useStyles();

  const selectedExportOption = exportTypeOptions.find((option) => option.key === formValues.exportType);
  const isAutoRefreshDisabled = selectedExportOption ? !selectedExportOption.supportsAutoRefresh : false;
  const autoRefreshTooltipText = isAutoRefreshDisabled
    ? 'Auto Refresh is not available for this export type yet.'
    : 'Automatically regenerate your base dataset with the last ingested player data.';

  const getExportLabel = (exportType?: string) => {
    switch (exportType as ExportType) {
      case ExportType.Iris:
        return "IRIS-GSC Base Dataset";
      case ExportType.Xandr:
        return "Xandr Dataset";
      default:
        return "GSC Base Dataset";
    }
  };

  return (
    <>
      <Field required
        className={styles.exportFormField}
        label={getExportLabel(formValues.exportType) + " Name"}
        validationState={showNameError ? "error" : "none"}
        validationMessage={showNameError ? `Please make sure the ${getExportLabel(formValues.exportType)} Name is not empty, is alphanumeric and doesn't contain whitespaces.` : ""}>
        <Input
          type="text"
          name="datasetName"
          value={formValues.datasetName}
          onChange={(_, data) => setFormValues({ ...formValues, datasetName: data.value })}
          placeholder={getExportLabel(formValues.exportType)}
        />
      </Field>

      <Field required
        className={styles.exportFormField}
        label="Description"
        validationState={showDescError ? "error" : "none"}
        validationMessage={showDescError ? "Please make sure the Description is not empty." : ""}>
        <Textarea
          name="description"
          value={formValues.description}
          onChange={(_, data) => setFormValues({ ...formValues, description: data.value })}
          placeholder={`Describe your ${getExportLabel(formValues.exportType)} export and the intended use-case. Example: All the users who are Ultimate Gamepass subscribers AND were active within last 30 days`}
        />
      </Field>
      
      {formValues.exportType === ExportType.Xandr && (
        <Field required
          className={styles.exportFormField}
          label="Shell ID"
          validationState={showShellIdError ? "error" : "none"}
          validationMessage={showShellIdError ? "Please enter a valid Shell ID." : ""}
          hint={
            <span>
              Enter the Shell ID for your Xandr Monetize segment.{' '}
              <Link href={XANDR_SETUP_DOC_URL} target="_blank" inline>
                How to get a Shell ID <Open16Regular />
              </Link>
            </span>
          }
        >
          <Input
            type="text"
            name="shellId"
            value={formValues.shellId}
            onChange={(_, data) => setFormValues({ ...formValues, shellId: data.value })}
            placeholder="e.g. 12345678"
          />
        </Field>
      )}

      <Field className={styles.exportFormField}>
        <Tooltip
          content={autoRefreshTooltipText}
          relationship="description"
          positioning="above-start"
        >
          <span>
            <Switch
              label={
                <span className={isAutoRefreshDisabled ? styles.disabledLabel : ''} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Enable Dataset Auto-Refresh
                  <Info16Regular />
                </span>
              }
              checked={formValues.isAutoRefresh}
              disabled={isAutoRefreshDisabled}
              onChange={(_, data) => setFormValues({ ...formValues, isAutoRefresh: data.checked })}
            />
          </span>
        </Tooltip>
      </Field>
      
      {Object.keys(requiredCheckboxItems).map((id) => {
        // Only show EXPORT_AUTO_REFRESH_EXPIRY checkbox when auto-refresh is enabled
        if (id === "EXPORT_AUTO_REFRESH_EXPIRY" && !formValues.isAutoRefresh) {
          return null;
        }
        return (
          <div key={id}>
            <Field 
              required={allCheckboxMetadata[id].required} 
              className={styles.exportFormField}>
              <Checkbox
                label={allCheckboxMetadata[id].displayText}
                name={id}
                checked={requiredCheckboxItems[id]}
                onChange={(_, data) => setRequiredCheckboxItems({ ...requiredCheckboxItems, [id]: data.checked === true })}
              />
            </Field>
          </div>
        );
      })}

      {showCheckboxError && (
        <div className={styles.requiredError}>
          You must agree to the terms before submitting the form.
        </div>
      )}
    </>
  );
};




const useStyles = makeStyles({
  dialogSurface: {
    marginTop: '30vh',
    alignSelf: 'flex-start',
  },
  requestButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
  },
  exportFormField: {
    marginBottom: '10px',
  },
  requiredError: {
    color: '#d97979',
    marginTop: '5px',
    marginBottom: '10px',
    fontStyle: 'italic',
    textAlign: 'right',
  },
  exportTypeDescription: {
    fontStyle: 'italic',
    fontSize: '0.9em',
    opacity: '0.7',
  },
  disabledLabel: {
    opacity: '0.6',
    cursor: 'not-allowed',
  },
  autoRefreshSubmitButton: {
    backgroundColor: '#0078d4',
    color: 'white',
    transitionProperty: 'background-color',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease',
    ':hover': {
      backgroundColor: '#005a9e',
    },
    ':disabled': {
      backgroundColor: '#0078d4',
    },
  },
  internalTestOption: {
    color: '#a9d8b9',
  }
}); 

export default ExportPopUp;